/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import NotificationPreferences from './NotificationPreferences';
import './NotificationPreferencesDialog.css';

/**
 * The notification preferences dialog, shared by the home dashboard and the
 * profile page so both open the same thing.
 *
 * Admins get one tab per facility: their own facility edits their global
 * preferences, every other facility edits that facility's preferences. Everyone
 * else just gets the preferences list.
 */
const NotificationPreferencesDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.institution_roles?.some(r => r.role === 'Admin');

  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);

  // Only admins see the facility tabs, so only they need the list.
  useEffect(() => {
    if (!open || !isAdmin || facilities.length > 0) return;
    axios.get('/api/facilities')
      .then(r => setFacilities(r.data || []))
      .catch(() => {});
  }, [open, isAdmin, facilities.length]);

  // Start each visit on the default tab rather than wherever it was left.
  useEffect(() => {
    if (!open) setSelectedFacilityId(null);
  }, [open]);

  if (!open) return null;

  const close = () => {
    setSelectedFacilityId(null);
    onClose();
  };

  const showFacilityTabs = isAdmin && facilities.length > 0;
  const myFacId = user?.facility?._id || user?.facility;
  const myFacility = facilities.find(f => String(f._id) === String(myFacId));

  // Exactly one tab per facility. The user's own facility maps to their global
  // preferences (facilityId = null) and is marked "(My Facility)"; other
  // facilities map to facility-specific preferences. When the user's facility is
  // undisclosed, every facility is shown as a facility-specific tab — never an
  // extra "My Facility" window.
  const tabs = [];
  if (showFacilityTabs) {
    if (myFacility) {
      tabs.push({ key: 'mine', facilityId: null, label: myFacility.facility_name, mine: true });
      facilities
        .filter(f => String(f._id) !== String(myFacId))
        .forEach(f => tabs.push({ key: String(f._id), facilityId: f._id, label: f.facility_name, mine: false }));
    } else {
      facilities.forEach(f => tabs.push({ key: String(f._id), facilityId: f._id, label: f.facility_name, mine: false }));
    }
  }

  // Resolve the active tab. A null selection picks the first tab (the
  // "My Facility"/global tab when disclosed, otherwise the first facility).
  const selKey = String(selectedFacilityId ?? '');
  const matches = tabs.some(t => String(t.facilityId ?? '') === selKey);
  const activeFacilityId = !showFacilityTabs
    ? null
    : (matches ? selectedFacilityId : (tabs[0]?.facilityId ?? null));

  return (
    <div className="notif-prefs-overlay" onClick={close}>
      <div className="notif-prefs-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="notif-prefs-header">
          <button type="button" className="notif-prefs-close" onClick={close} aria-label="Close" title="Close">
            <CloseIcon fontSize="inherit" />
          </button>
          <h3>Notification Preferences</h3>
          <p>Choose which notifications you receive via in-app alerts and email.</p>
        </div>

        {showFacilityTabs && tabs.length > 0 && (
          <div className="notif-prefs-tabs">
            {tabs.map(t => {
              const isActive = String(t.facilityId ?? '') === String(activeFacilityId ?? '');
              return (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={isActive}
                  className={`notif-prefs-tab${isActive ? ' active' : ''}`}
                  onClick={() => setSelectedFacilityId(t.facilityId)}
                >
                  {t.label}
                  {t.mine && <span className="notif-prefs-tab-sub">(My Facility)</span>}
                </button>
              );
            })}
          </div>
        )}

        <NotificationPreferences
          facilityId={activeFacilityId}
          onClose={close}
          hideHeader
        />
      </div>
    </div>
  );
};

export default NotificationPreferencesDialog;
